import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
} from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ErrorHandlingService } from 'src/app/core/services/error-handling.service';
import { NotificationsService } from 'angular2-notifications';
import { WidgetsApiService } from 'src/app/core/services/api/widgets-api.service';
import { finalize, forkJoin } from 'rxjs';
import { Widget, WidgetGroup } from 'src/app/shared/models/widget.interface';
import { ClientType } from 'src/app/shared/enums/client-type.enum';
import { UntilDestroy } from '@ngneat/until-destroy';

/** Flat widget entry enriched with a selected flag for the UI. */
interface WidgetEntry {
  id: number;
  name: string;
  display_order: number;
  selected: boolean;
}

/** One "page" group rendered in the list. */
interface PageGroup {
  page: string;
  widgets: WidgetEntry[];
}

/** Maps ClientType to the filter_by_group query parameter value. */
const CLIENT_TYPE_TO_GROUP: Record<string, string> = {
  [ClientType.Broker]: 'broker_dashboard',
  [ClientType.Agent]: 'agent_dashboard',
  [ClientType.Dealer]: 'dealer_dashboard',
  [ClientType.DealerGroup]: 'dealer_dashboard',
};

@UntilDestroy()
@Component({
  selector: 'app-user-widget-control-modal',
  templateUrl: './user-widget-control-modal.component.html',
  styleUrls: ['./user-widget-control-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserWidgetControlModalComponent {
  userId!: number;
  userType?: ClientType;

  isLoading = false;
  isSubmitLoading = false;

  pageGroups: PageGroup[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    public config: DynamicDialogConfig,
    private errorHandlingService: ErrorHandlingService,
    private notificationService: NotificationsService,
    private ref: DynamicDialogRef,
    private widgetsApi: WidgetsApiService,
  ) {
    this.userId = this.config?.data?.userId;
    this.userType = this.config?.data?.userType;
    this.loadWidgets();
  }

  get allSelected(): boolean {
    return this.pageGroups.every((pg) => pg.widgets.every((w) => w.selected));
  }

  get noneSelected(): boolean {
    return this.pageGroups.every((pg) => pg.widgets.every((w) => !w.selected));
  }

  get someSelected(): boolean {
    return !this.allSelected && !this.noneSelected;
  }

  toggleAll(selected: boolean): void {
    this.pageGroups.forEach((pg) =>
      pg.widgets.forEach((w) => (w.selected = selected)),
    );
  }

  private loadWidgets(): void {
    this.isLoading = true;
    const group = this.userType ? CLIENT_TYPE_TO_GROUP[this.userType] : undefined;

    forkJoin({
      catalog: this.widgetsApi.getWidgets(group ? { filter_by_group: group } : {}),
      userWidgets: this.widgetsApi.getUserWidgets(this.userId),
    })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: ({ catalog, userWidgets }) => {
          const userWidgetIds = new Set<number>(
            (userWidgets?.data ?? []).map((w: Widget) => w.id),
          );
          // userWidgets is empty → no restriction → treat all as selected
          const showAll = userWidgetIds.size === 0;

          this.pageGroups = this.buildPageGroups(
            catalog?.data ?? [],
            userWidgetIds,
            showAll,
          );
        },
        error: (err) => {
          this.errorHandlingService.errorHandling(err);
        },
      });
  }

  private buildPageGroups(
    groups: WidgetGroup[],
    userWidgetIds: Set<number>,
    showAll: boolean,
  ): PageGroup[] {
    const pageMap = new Map<string, WidgetEntry[]>();

    for (const group of groups) {
      for (const page of group.pages) {
        if (!pageMap.has(page.page)) {
          pageMap.set(page.page, []);
        }
        for (const widget of page.widgets) {
          pageMap.get(page.page)!.push({
            id: widget.id,
            name: widget.name,
            display_order: widget.display_order,
            selected: showAll || userWidgetIds.has(widget.id),
          });
        }
      }
    }

    return Array.from(pageMap.entries()).map(([page, widgets]) => ({
      page,
      widgets: widgets.sort((a, b) => a.display_order - b.display_order),
    }));
  }

  cancelClicked(): void {
    this.ref.close();
  }

  saveClicked(): void {
    // Collect selected widget IDs; empty array = reset to "all"
    const selectedIds: number[] = [];
    this.pageGroups.forEach((pg) =>
      pg.widgets.forEach((w) => {
        if (w.selected) selectedIds.push(w.id);
      }),
    );

    // Determine if all widgets are selected — if so, send empty to reset
    const allWidgetIds: number[] = [];
    this.pageGroups.forEach((pg) =>
      pg.widgets.forEach((w) => allWidgetIds.push(w.id)),
    );
    const isAll = selectedIds.length === allWidgetIds.length;
    const payload = isAll ? [] : selectedIds;

    this.isSubmitLoading = true;
    this.widgetsApi
      .setUserWidgets(this.userId, payload)
      .pipe(finalize(() => {
        this.isSubmitLoading = false;
        this.cdr.detectChanges();
      }))
      .subscribe({
        next: () => {
          this.notificationService.success('Success', 'Widget settings saved.');
          this.ref.close(true);
        },
        error: (err) => {
          this.errorHandlingService.errorHandling(err);
        },
      });
  }
}
